import {FC, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import moment from 'moment'
import {KTSVG} from '../../../../_metronic/helpers'

const API_URL = process.env.REACT_APP_API_URL

export type SeriesFutureAppointment = {
  code: number
  recordId: string
  startTime: string
  endTime: string
  language: string
  communicationType: string
}

type CancelSeriesSelectionModalProps = {
  appointmentId: number
  onClose: () => void
  onConfirm: (selectedAppointmentIds: number[]) => void
}

export const normalizeSeriesAppointment = (
  appointment: Record<string, unknown>
): SeriesFutureAppointment => ({
  code: Number(appointment.code ?? appointment.Code),
  recordId: String(appointment.recordId ?? appointment.RecordId ?? ''),
  startTime: String(appointment.startTime ?? appointment.StartTime ?? ''),
  endTime: String(appointment.endTime ?? appointment.EndTime ?? ''),
  language: String(appointment.language ?? appointment.Language ?? ''),
  communicationType: String(
    appointment.communicationType ?? appointment.CommunicationType ?? ''
  ),
})

const CancelSeriesSelectionModal: FC<CancelSeriesSelectionModalProps> = ({
  appointmentId,
  onClose,
  onConfirm,
}) => {
  const [appointments, setAppointments] = useState<SeriesFutureAppointment[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    const fetchAppointments = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const {data} = await axios.get(
          `${API_URL}/Appoinment/series/${appointmentId}/future-cancellable`
        )
        const rawAppointments =
          data.result?.appointments ?? data.result?.Appointments ?? data.Result?.appointments ?? data.Result?.Appointments

        if (data.status === 'S' && Array.isArray(rawAppointments)) {
          setAppointments(rawAppointments.map(normalizeSeriesAppointment))
        } else {
          setError(data.text || 'Failed to load appointments')
        }
      } catch (err: any) {
        setError(err?.response?.data?.text || 'Failed to load appointments')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAppointments()
  }, [appointmentId])

  const allSelected = useMemo(
    () => appointments.length > 0 && selectedIds.length === appointments.length,
    [appointments.length, selectedIds.length]
  )

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([])
    } else {
      setSelectedIds(appointments.map((a) => a.code))
    }
  }

  const toggleSelect = (code: number) => {
    setSelectedIds((prev) =>
      prev.includes(code) ? prev.filter((id) => id !== code) : [...prev, code]
    )
  }

  const formatDateTime = (startTime: string) => {
    return moment(startTime).format('MMM DD, YYYY • h:mm A')
  }

  const handleConfirm = () => {
    if (selectedIds.length === 0) return
    onConfirm(selectedIds)
  }

  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_cancel_series_selection'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-900px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>Cancel Appointments</h2>
              <div
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{cursor: 'pointer'}}
                onClick={onClose}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>

            <div className='modal-body'>
              <p className='text-muted mb-5'>
                Select multiple upcoming recurring appointments from the list below to cancel them
                in bulk.
              </p>

              {isLoading ? (
                <div className='text-center py-10'>
                  <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                  Loading appointments...
                </div>
              ) : error ? (
                <div className='alert alert-danger'>{error}</div>
              ) : appointments.length === 0 ? (
                <div className='text-center py-10 text-muted'>
                  No upcoming appointments available for cancellation in this series.
                </div>
              ) : (
                <div className='table-responsive border rounded'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted border-bottom'>
                        <th className='w-40px ps-4'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            aria-label='Select all appointments'
                          />
                        </th>
                        <th>Appointment ID</th>
                        <th>Date &amp; Time</th>
                        <th>Language</th>
                        <th>Communication Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((appointment) => (
                        <tr key={appointment.code}>
                          <td className='ps-4'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              checked={selectedIds.includes(appointment.code)}
                              onChange={() => toggleSelect(appointment.code)}
                              aria-label={`Select appointment ${appointment.code}`}
                            />
                          </td>
                          <td className='fw-semibold'>{appointment.code}</td>
                          <td>{formatDateTime(appointment.startTime)}</td>
                          <td>{appointment.language || '-'}</td>
                          <td>{appointment.communicationType || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!isLoading && appointments.length > 0 && (
                <div className='mt-4 text-muted fw-semibold'>
                  {selectedIds.length} of {appointments.length} appointments selected
                </div>
              )}

              <div className='d-flex justify-content-end gap-3 mt-6'>
                <button className='btn btn-light' onClick={onClose}>
                  Close
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  disabled={selectedIds.length === 0 || isLoading}
                  onClick={handleConfirm}
                >
                  Cancel selected appointments
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export {CancelSeriesSelectionModal}
